// Диалог создания/редактирования записи электронного журнала (старый надежный механизм)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Autocomplete,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  QrCodeScanner as BarcodeIcon,
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  SwapHoriz as SwapIcon,
  KeyboardReturn as KeyboardReturnIcon
} from '@mui/icons-material';
import axios from 'axios';
import { JournalEntry } from '../types';
import { API_BASE } from '../constants';

const COMMENTS_LIST = [
  'Реализовано',
  'Обменял МП',
  'Перемещение',
  'Возврат по письму от КМ'
];

interface NomenclatureItem {
  productZid: string;
  productSku: string;
  productName: string;
  category: string;
  barcode?: string;
  producer?: string;
  manager?: string;
  price?: number;
  stock?: number;
  partCoeff?: number;
  productFolder?: string;
  productType?: string;
  priceGroup?: string;
  rxotc?: string;
}

interface JournalEntryDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (entry: JournalEntry) => Promise<boolean>;
  editEntry?: JournalEntry | null;
  pharmacyNum: string | null;
  pharmacyGuid: string | null;
}

export const JournalEntryDialog: React.FC<JournalEntryDialogProps> = ({
  open,
  onClose,
  onSave,
  editEntry,
  pharmacyNum,
  pharmacyGuid
}) => {
  const theme = useTheme();
  const isEdit = !!editEntry;

  // ─── Product search state ──────────────────────────────────────────────────
  const [options, setOptions] = useState<NomenclatureItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [localInputValue, setLocalInputValue] = useState('');
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);

  // ─── Auto-filled product fields ────────────────────────────────────────────
  const [selectedProduct, setSelectedProduct] = useState<NomenclatureItem | null>(null);
  const [nomenclatureCode, setNomenclatureCode] = useState('');
  const [barcode, setBarcode] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [materialName, setMaterialName] = useState('');
  const [managerName, setManagerName] = useState('');

  // ─── Manual fields ─────────────────────────────────────────────────────────
  const [serialNumber, setSerialNumber] = useState('');
  const [isManualSeries, setIsManualSeries] = useState(false);
  const [availableSeries, setAvailableSeries] = useState<any[]>([]);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [quantity, setQuantity] = useState<number | ''>('');
  const [quantityPcs, setQuantityPcs] = useState<number | ''>('');
  const [price, setPrice] = useState<number>(0);
  const [comment, setComment] = useState('');
  /** RXOTC из актуальной номенклатуры 1С при редактировании (перекрывает сохранённое в записи) */
  const [freshRxotc, setFreshRxotc] = useState('');

  // ─── Form state ────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const isSeriesRequired = !!categoryName;

  const coeff = selectedProduct?.partCoeff !== undefined
    ? selectedProduct.partCoeff
    : (editEntry?.partCoeff !== undefined ? editEntry.partCoeff : 0);

  const isFallbackProduct = !isEdit && selectedProduct !== null && selectedProduct.stock === 0;

  const isBarcodeLocked = categoryName === 'Сангигиена NEW' || categoryName === 'Мед.изделия New';

  const calculatedSum = React.useMemo(() => {
    const qtyVal = isFallbackProduct ? 0 : (quantity !== '' ? Number(quantity) : 0);
    const pcsVal = quantityPcs !== '' ? Number(quantityPcs) : 0;
    let sum = qtyVal * price;
    if (pcsVal > 0) {
      if (coeff > 0) {
        sum += pcsVal * (price / coeff);
      } else {
        sum += pcsVal * price;
      }
    }
    return Math.round(sum * 100) / 100;
  }, [quantity, quantityPcs, price, coeff, isFallbackProduct]);

  // ─── Init / Reset ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setFreshRxotc('');

    if (editEntry) {
      setNomenclatureCode(editEntry.nomenclatureCode || '');
      setBarcode(editEntry.barcode || '');
      setCategoryName(editEntry.categoryName || '');
      setMaterialName(editEntry.materialName || '');
      setSerialNumber(editEntry.serialNumber || '');
      setExpiryDate(editEntry.expiryDate ? editEntry.expiryDate.split('T')[0] : '');
      setQuantity(editEntry.quantity || '');
      setQuantityPcs(editEntry.quantityPcs || '');
      setPrice(editEntry.price || 0);
      setManagerName(editEntry.managerName || '');
      setComment(editEntry.comment || '');
      setIsManualSeries(true);
      setOpenMenu(false);

      if (editEntry.nomenclatureCode && pharmacyGuid) {
        setSeriesLoading(true);
        axios.get(`${API_BASE}/materials`, {
          params: {
            pharmacyGuid,
            search: editEntry.nomenclatureCode,
            limit: 1
          }
        }).then(res => {
          const material = res.data?.materials?.[0];
          if (material && material.RXOTC) setFreshRxotc(material.RXOTC);
          if (material && material.ProductZid) {
            return axios.get(`${API_BASE}/journal-user/series/${material.ProductZid}`);
          }
          throw new Error('Product not found');
        }).then(seriesRes => {
          if (seriesRes.data?.success) {
            const list = seriesRes.data.data || [];
            setAvailableSeries(list);
            const exists = list.some((s: any) => {
              const name = typeof s === 'string' ? s : s.SeriesName;
              return name && name.toLowerCase() === editEntry.serialNumber.trim().toLowerCase();
            });
            setIsManualSeries(!exists);
          }
        }).catch(err => {
          console.warn('Ошибка загрузки серий:', err.message);
          setAvailableSeries([]);
          setIsManualSeries(true);
        }).finally(() => {
          setSeriesLoading(false);
        });
      }
    } else {
      setSelectedProduct(null);
      setLocalInputValue('');
      setSearchTriggered(false);
      setOpenMenu(false);
      setNomenclatureCode('');
      setBarcode('');
      setCategoryName('');
      setMaterialName('');
      setSerialNumber('');
      setIsManualSeries(false);
      setAvailableSeries([]);
      setExpiryDate('');
      setQuantity('');
      setQuantityPcs('');
      setPrice(0);
      setComment('');
      setManagerName('');
      setFormError(null);
    }
  }, [open, editEntry, pharmacyGuid]);

  const parse1CExpirationDate = useCallback((dateStr: string): string => {
    if (!dateStr) return '';
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
      const onlyDate = dateStr.split(' ')[0];
      const [day, month, year] = onlyDate.split('.');
      if (day && month && year) {
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    } catch { }
    return '';
  }, []);

  // ─── Product select ────────────────────────────────────────────────────────
  const handleProductSelect = useCallback(async (product: NomenclatureItem | null) => {
    setSelectedProduct(product);
    setOpenMenu(false);
    if (!product) {
      setNomenclatureCode(''); setBarcode(''); setCategoryName('');
      setMaterialName(''); setManagerName(''); setPrice(0);
      setAvailableSeries([]); return;
    }
    setNomenclatureCode(product.productSku);
    setCategoryName(product.category);
    setMaterialName(`${product.productName}${product.producer ? ' / ' + product.producer : ''}`);
    setManagerName(product.manager || 'Не указан');

    const barcodeVal = product.barcode && product.barcode !== '-' ? product.barcode : '';
    setBarcode(barcodeVal);
    setPrice(product.price || 0);
    setQuantity(product.stock === 0 ? 0 : '');
    setQuantityPcs('');

    setSeriesLoading(true);
    try {
      const seriesRes = await axios.get(`${API_BASE}/journal-user/series/${product.productZid}`);
      if (seriesRes.data?.success) {
        setAvailableSeries(seriesRes.data.data || []);
        setIsManualSeries(false);
      }
    } catch { setAvailableSeries([]); setIsManualSeries(true); }
    finally { setSeriesLoading(false); }
  }, []);

  // ─── Product autocomplete search trigger ───────────────────────────────────
  const triggerSearch = async (query: string) => {
    if (query.trim().length < 2) { setOptions([]); setOpenMenu(false); return; }
    setSearchLoading(true);
    setSearchTriggered(true);
    setOptions([]);
    setOpenMenu(true);
    try {
      const res = await axios.get(`${API_BASE}/materials`, {
        params: {
          pharmacyGuid,
          search: query,
          limit: 50
        }
      });

      if (res.data?.success && Array.isArray(res.data.materials)) {
        const mapped = res.data.materials.map((m: any) => ({
          productZid: m.ProductZid,
          productSku: m.ProductSku,
          productName: m.ProductName,
          barcode: m.Barcode && m.Barcode !== '-' ? m.Barcode : '',
          category: m.Category || 'Без категории',
          producer: m.Producer || '',
          manager: m.Manager || 'Не указан',
          price: m.StockPrice || 0,
          stock: m.StockCount || 0,
          partCoeff: m.PartCoeff || 0,
          productFolder: m.ProductFolder || '',
          productType: m.ProductType || '',
          priceGroup: m.PriceGroup || '',
          rxotc: m.RXOTC || ''
        }));
        setOptions(mapped);
      } else {
        setOptions([]);
      }
      setOpenMenu(true);
    } catch (err) {
      console.error('Ошибка поиска:', err);
      setOptions([]);
      setOpenMenu(true);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleInputChange = (_event: React.SyntheticEvent, newValue: string, reason: string) => {
    setLocalInputValue(newValue);
    if (reason === 'clear') {
      setSearchTriggered(false);
      setOpenMenu(false);
      setOptions([]);
      return;
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.stopPropagation();
      if (localInputValue.trim().length >= 2) {
        triggerSearch(localInputValue.trim());
      }
    }
  };

  // ─── Series change ─────────────────────────────────────────────────────────
  const handleSeriesChange = (value: string) => {
    setSerialNumber(value);
    if (value && availableSeries.length > 0) {
      const matched = availableSeries.some(s => {
        const name = typeof s === 'string' ? s : s.SeriesName;
        return name && name.toLowerCase() === value.trim().toLowerCase();
      });
      if (!matched) { setIsManualSeries(true); setComment('Серия не совпадает'); }
      else { setIsManualSeries(false); if (comment === 'Серия не совпадает') setComment(''); }
    }
  };

  // ─── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setFormError(null);
    if (!materialName) { setFormError('Выберите товар'); return; }
    if (isSeriesRequired && !serialNumber.trim()) { setFormError('Поле "Серия" обязательно для заполнения'); return; }
    if (!expiryDate) { setFormError('Укажите срок годности'); return; }

    const qtyVal = quantity !== '' ? Number(quantity) : 0;
    const pcsVal = quantityPcs !== '' ? Number(quantityPcs) : 0;

    if (!isEdit) {
      if (isFallbackProduct) {
        if (pcsVal <= 0) {
          setFormError('Укажите количество в штуках');
          return;
        }
      } else if (qtyVal <= 0 && pcsVal <= 0) {
        setFormError('Укажите количество (заполните поле Количество уп. или Количество шт.)');
        return;
      }
    }

    if (qtyVal < 0 || pcsVal < 0) {
      setFormError('Количество не может быть отрицательным');
      return;
    }

    setSaving(true);
    try {
      const success = await onSave({
        id: editEntry?.id,
        pharmacyNum: pharmacyNum || '000',
        pharmacyGuid: pharmacyGuid || '',
        nomenclatureCode,
        barcode,
        categoryName,
        materialName,
        serialNumber: serialNumber.trim(),
        expiryDate,
        quantity: isFallbackProduct ? 0 : qtyVal,
        quantityPcs: pcsVal,
        partCoeff: coeff,
        price: Number(price),
        totalSum: calculatedSum,
        managerName,
        comment,
        producer: selectedProduct?.producer || editEntry?.producer || '',
        cityId: localStorage.getItem('cityId') || editEntry?.cityId || '',
        priceGroup: selectedProduct?.priceGroup || editEntry?.priceGroup || '',
        rxotc: selectedProduct?.rxotc || freshRxotc || editEntry?.rxotc || ''
      });
      if (success) onClose();
      else setFormError('Не удалось сохранить. Проверьте ограничения периода ввода.');
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Ошибка соединения с сервером');
    } finally { setSaving(false); }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      transitionDuration={0}
      PaperProps={{ sx: { bgcolor: 'white', borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* ══ HEADER ═══════════════════════════════════════════════════════════ */}
      <Box sx={{
        px: 3, pt: 3, pb: 1.5,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        bgcolor: 'white',
        flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
            {isEdit ? `Редактирование записи №${editEntry?.id}` : 'Новая запись в журнал'}
          </Typography>
          {pharmacyNum && (
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontWeight: 500 }}>
              Аптека №{pharmacyNum}
            </Typography>
          )}
        </Box>

        <IconButton
          onClick={onClose}
          disabled={saving}
          size="small"
          sx={{
            color: '#64748b',
            transition: '0.2s',
            '&:hover': { color: '#ef4444' }
          }}
        >
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>

      {/* ══ SEARCH AREA ═════════════════════════════════════════════════════ */}
      {!isEdit && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Autocomplete
            id="journal-product-search"
            size="medium"
            options={options}
            loading={searchLoading}
            getOptionLabel={opt => typeof opt === 'string' ? opt : (opt?.productName || '')}
            filterOptions={x => x}
            value={selectedProduct}
            inputValue={localInputValue}
            onInputChange={handleInputChange}
            onChange={(_, v) => handleProductSelect(v)}
            open={openMenu}
            onOpen={() => {
              if (searchTriggered) {
                setOpenMenu(true);
              }
            }}
            onClose={() => setOpenMenu(false)}
            renderOption={(props, opt) => (
              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2, borderBottom: '1px solid #f0f0f0' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, mr: 2, overflow: 'hidden' }}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: 'grey.900' }}>
                    {opt.productName}
                  </Typography>

                  {opt.producer && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                      Производитель: {opt.producer}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                    {opt.barcode && opt.barcode !== '-' && (
                      <Chip label={`ШК: ${opt.barcode}`} size="small" variant="outlined" sx={{ fontSize: '0.68rem', height: 18, bgcolor: 'grey.50' }} />
                    )}
                    <Chip label={opt.category} size="small" sx={{ fontSize: '0.68rem', height: 18, bgcolor: '#fce7f3', color: '#be185d', fontWeight: 600 }} />
                    <Chip label={`Менеджер: ${opt.manager || 'Не указан'}`} size="small" variant="outlined" sx={{ fontSize: '0.68rem', height: 18 }} />
                    {opt.productFolder && (
                      <Chip label={`Группа: ${opt.productFolder}`} size="small" variant="outlined" sx={{ fontSize: '0.68rem', height: 18, bgcolor: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' }} />
                    )}
                    {opt.productType && (
                      <Chip label={`Тип: ${opt.productType}`} size="small" variant="outlined" sx={{ fontSize: '0.68rem', height: 18, bgcolor: '#f0fdf4', color: '#166534', borderColor: '#bbf7d0' }} />
                    )}
                    {opt.priceGroup && (
                      <Chip label={`Цен. группа: ${opt.priceGroup}`} size="small" variant="outlined" sx={{ fontSize: '0.68rem', height: 18, bgcolor: '#fff7ed', color: '#c2410c', borderColor: '#ffedd5' }} />
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#2e7d32', px: 1, py: 0.5, borderRadius: 1, bgcolor: '#e8f5e9' }}>
                    {opt.price ? opt.price.toLocaleString('ru-RU') : 0} ₸
                  </Typography>
                  {opt.stock !== undefined && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      Остаток: {opt.stock} шт.
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            renderInput={params => (
              <TextField {...params} placeholder="Введите наименование, код или штрихкод и нажмите Enter..."
                onKeyDown={handleKeyDown}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1, fontSize: 20 }} />,
                  endAdornment: (
                    <React.Fragment>
                      {searchLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                      {!selectedProduct && localInputValue.trim().length >= 2 && !searchTriggered && (
                        <InputAdornment position="end">
                          <Chip
                            label="Enter"
                            size="small"
                            icon={<KeyboardReturnIcon style={{ fontSize: 13 }} />}
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              bgcolor: 'grey.100',
                              color: 'text.secondary',
                              border: '1px solid',
                              borderColor: 'grey.300',
                              cursor: 'default',
                              '& .MuiChip-label': { px: 0.75 },
                              '& .MuiChip-icon': { ml: 0.5 }
                            }}
                          />
                        </InputAdornment>
                      )}
                    </React.Fragment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    bgcolor: '#f8fafc',
                    '& fieldset': {
                      borderColor: '#cbd5e1'
                    },
                    '&:hover fieldset': {
                      borderColor: '#94a3b8'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0284c7'
                    }
                  }
                }}
              />
            )}
            noOptionsText={
              localInputValue && !searchTriggered
                ? 'Нажмите Enter для поиска'
                : 'Товары не найдены'
            }
            loadingText="Поиск..."
          />
        </Box>
      )}

      {/* ══ CENTRAL BORDERED AREA ════════════════════════════════════════════ */}
      {!(materialName || isEdit) ? (
        <Box sx={{
          mx: 3,
          mb: 3,
          py: 7,
          px: 4,
          border: '1px dashed #cbd5e1',
          borderRadius: 2.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fafafa',
          textAlign: 'center',
          boxShadow: 'none',
          transition: 'all 0.3s'
        }}>
          <SearchIcon sx={{ fontSize: 44, color: '#94a3b8', mb: 2, opacity: 0.7 }} />
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#334155', mb: 0.5 }}>
            Поиск и выбор товара
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 380, px: 2, lineHeight: 1.5, fontSize: '0.875rem' }}>
            Введите наименование, штрих-код или код товара в поле выше и нажмите <strong>Enter</strong> для поиска. Затем выберите нужную позицию.
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          mx: 3,
          mb: 2.5,
          border: '1px solid #e2e8f0',
          borderRadius: 2.5,
          bgcolor: 'white',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <Box sx={{ p: 2.5, borderBottom: '1px solid #f1f5f9', bgcolor: 'white' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.3 }}>
              {selectedProduct ? selectedProduct.productName : (materialName.split(' / ')[0] || materialName)}
            </Typography>
            {(selectedProduct?.producer || materialName.split(' / ')[1]) && (
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontWeight: 500 }}>
                Производитель: <span style={{ color: '#334155', fontWeight: 600 }}>{selectedProduct ? selectedProduct.producer : materialName.split(' / ')[1]}</span>
              </Typography>
            )}
          </Box>

          <Grid container>
            <Grid item xs={12} md={7.5} sx={{ p: 2.5, borderRight: { md: '1px solid #f1f5f9' } }}>
              {formError && (
                <Alert severity="error" onClose={() => setFormError(null)} sx={{ mb: 2.5, borderRadius: 1.5 }}>
                  {formError}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Штрих-код (ШК)"
                    value={barcode}
                    required
                    onChange={e => setBarcode(e.target.value)}
                    disabled={isBarcodeLocked}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BarcodeIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        bgcolor: '#fafafa',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                        '&.Mui-focused fieldset': { borderColor: '#0284c7' }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  {seriesLoading ? (
                    <TextField
                      fullWidth
                      size="small"
                      disabled
                      label="Серия"
                      value="Загрузка серий из 1С..."
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CircularProgress size={16} sx={{ color: '#0284c7' }} />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          bgcolor: '#fafafa',
                          '& fieldset': { borderColor: '#e2e8f0' }
                        }
                      }}
                    />
                  ) : isManualSeries || availableSeries.length === 0 ? (
                    <TextField
                      fullWidth
                      size="small"
                      required={isSeriesRequired}
                      label="Серия"
                      value={serialNumber}
                      onChange={e => handleSeriesChange(e.target.value)}
                      error={isSeriesRequired && !serialNumber.trim()}
                      placeholder="Серия"
                      InputProps={{
                        endAdornment: availableSeries.length > 0 ? (
                          <InputAdornment position="end">
                            <Tooltip title="Выбрать из БД">
                              <IconButton size="small" onClick={() => setIsManualSeries(false)}>
                                <SwapIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ) : undefined
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          bgcolor: '#fafafa',
                          '& fieldset': { borderColor: '#e2e8f0' },
                          '&:hover fieldset': { borderColor: '#cbd5e1' },
                          '&.Mui-focused fieldset': { borderColor: '#0284c7' }
                        }
                      }}
                    />
                  ) : (
                    <FormControl fullWidth size="small" required={isSeriesRequired} error={isSeriesRequired && !serialNumber.trim()}>
                      <InputLabel>Серия</InputLabel>
                      <Select
                        value={serialNumber}
                        label="Series"
                        onChange={e => {
                          const val = e.target.value;
                          if (val === '__MANUAL__') {
                            setIsManualSeries(true);
                            setSerialNumber('');
                          } else {
                            setSerialNumber(val);
                            if (comment === 'Серия не совпадает') setComment('');

                            const selectedObj = availableSeries.find(s => {
                              const name = typeof s === 'string' ? s : s.SeriesName;
                              return name === val;
                            });
                            if (selectedObj && typeof selectedObj !== 'string' && selectedObj.ExpirationDate) {
                              const parsedDate = parse1CExpirationDate(selectedObj.ExpirationDate);
                              if (parsedDate) {
                                setExpiryDate(parsedDate);
                              }
                            }
                          }
                        }}
                        sx={{
                          borderRadius: 1.5,
                          bgcolor: '#fafafa',
                          '& fieldset': { borderColor: '#e2e8f0' },
                          '&:hover fieldset': { borderColor: '#cbd5e1' },
                          '&.Mui-focused fieldset': { borderColor: '#0284c7' }
                        }}
                      >
                        {availableSeries.map((s, index) => {
                          const name = typeof s === 'string' ? s : s.SeriesName;
                          const zid = typeof s === 'string' ? index : (s.SeriesZid || index);
                          return (
                            <MenuItem key={zid} value={name}>
                              {name}
                            </MenuItem>
                          );
                        })}
                        <Divider />
                        <MenuItem value="__MANUAL__" sx={{ color: 'secondary.main', fontStyle: 'italic', fontSize: '0.875rem' }}>
                          + Ввести вручную
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Срок годности"
                    type="date"
                    value={expiryDate}
                    onChange={e => setExpiryDate(e.target.value)}
                    required
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        bgcolor: '#fafafa',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                        '&.Mui-focused fieldset': { borderColor: '#0284c7' }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Tooltip
                    title={isFallbackProduct ? 'Товара нет в остатках аптеки - упаковки недоступны. Укажите количество в штуках.' : ''}
                    placement="top"
                    arrow
                  >
                    <span>
                      <TextField
                        fullWidth
                        size="small"
                        label="Количество уп."
                        type="number"
                        value={isFallbackProduct ? 0 : quantity}
                        onChange={e => !isFallbackProduct && setQuantity(e.target.value === '' ? '' : Number(e.target.value))}
                        disabled={isFallbackProduct}
                        inputProps={{ min: 0 }}
                        error={!isFallbackProduct && quantity !== '' && quantity < 0}
                        placeholder="Количество уп."
                        helperText={isFallbackProduct ? 'Нет в остатках этой аптеки' : undefined}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <InventoryIcon sx={{ fontSize: 18, color: isFallbackProduct ? '#cbd5e1' : '#94a3b8' }} />
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            bgcolor: isFallbackProduct ? '#f1f5f9' : '#fafafa',
                            '& fieldset': { borderColor: isFallbackProduct ? '#e2e8f0' : '#e2e8f0' },
                            '&:hover fieldset': { borderColor: isFallbackProduct ? '#e2e8f0' : '#cbd5e1' },
                            '&.Mui-focused fieldset': { borderColor: '#0284c7' }
                          }
                        }}
                      />
                    </span>
                  </Tooltip>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Количество шт."
                    type="number"
                    value={quantityPcs}
                    onChange={e => setQuantityPcs(e.target.value === '' ? '' : Number(e.target.value))}
                    inputProps={{ min: 0 }}
                    error={quantityPcs !== '' && quantityPcs < 0}
                    placeholder="Количество шт."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <InventoryIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        bgcolor: '#fafafa',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                        '&.Mui-focused fieldset': { borderColor: '#0284c7' }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="comment-label">Комментарий</InputLabel>
                    <Select
                      labelId="comment-label"
                      value={comment}
                      label="Комментарий"
                      onChange={e => setComment(e.target.value)}
                      disabled={comment === 'Серия не совпадает' && isManualSeries}
                      sx={{
                        borderRadius: 1.5,
                        bgcolor: '#fafafa',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                        '&.Mui-focused fieldset': { borderColor: '#0284c7' }
                      }}
                    >
                      <MenuItem value=""><em>Нет комментария</em></MenuItem>
                      {comment === 'Серия не совпадает' && (
                        <MenuItem value="Серия не совпадает">
                          <em>Серия не совпадает</em>
                        </MenuItem>
                      )}
                      {COMMENTS_LIST.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={4.5} sx={{ bgcolor: '#f8fafc', p: 2.5, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.68rem', display: 'block', mb: 1.5 }}>
                  Расчёт стоимости
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>Цена за ед.</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>{price.toLocaleString('ru-RU')} ₸</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>Количество</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                      {quantity || 0} уп.
                      {Number(quantityPcs) > 0 ? ` / ${quantityPcs} шт.` : ''}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1, borderStyle: 'dashed', borderColor: '#e2e8f0' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-baseline', mt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>Итого</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                      {calculatedSum.toLocaleString('ru-RU')} <span style={{ color: '#0284c7', fontWeight: 700 }}>₸</span>
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.68rem', display: 'block', mb: 1.5 }}>
                  Характеристики
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {nomenclatureCode && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>Код товара</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155', fontFamily: 'monospace' }}>{nomenclatureCode}</Typography>
                    </Box>
                  )}
                  {coeff > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>Кол-во в упаковке</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155' }}>{coeff} шт.</Typography>
                    </Box>
                  )}
                  {categoryName && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>Категория</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155' }}>
                        {categoryName.replace(' New', '').replace(' NEW', '')}
                      </Typography>
                    </Box>
                  )}
                  {managerName && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>Менеджер</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155' }}>{managerName}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* ══ FOOTER ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pb: 3, pt: 1 }}>
        <Button
          variant="text"
          onClick={onClose}
          disabled={saving}
          startIcon={<ArrowBackIcon />}
          sx={{
            textTransform: 'none',
            color: '#64748b',
            fontWeight: 600,
            px: 2,
            py: 1,
            borderRadius: 1.5,
            '&:hover': {
              color: '#0f172a',
              bgcolor: '#f1f5f9'
            }
          }}
        >
          Отмена
        </Button>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {materialName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
              <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700, fontSize: '0.75rem' }}>
                Товар выбран
              </Typography>
            </Box>
          )}
          <Button
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={saving || !materialName}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{
              textTransform: 'none',
              px: 3.5,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5,
              bgcolor: '#0284c7',
              color: 'white',
              '&:hover': { bgcolor: '#0369a1' },
              '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' }
            }}
          >
            {saving ? 'Сохранение...' : 'Сохранить запись'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
